import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  DateCalendar,
  type DateCalendarProps,
} from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  PickersDay,
  type PickersDayProps,
} from '@mui/x-date-pickers/PickersDay';
import {
  getDateAdapterLocale,
  getIntlLocaleTag,
  mapBackendLanguageToApp,
} from '@src/shared';
import { DATE_FORMAT } from '@src/shared/lib';
import type { DateRangeValue } from '@src/shared/lib/dateRangePresets';
import { parseDateInput } from '@src/shared/lib/parseDateInput';
import { Button, IconButton } from '@src/shared/ui/Button';
import TextField from '@src/shared/ui/inputs/TextInput';
import Box from '@src/shared/ui/layout/Box';
import Grid from '@src/shared/ui/layout/Grid';
import Stack from '@src/shared/ui/layout/Stack';
import Typography from '@src/shared/ui/Typography';
import {
  addMonths,
  endOfMonth,
  format,
  isSameDay,
  isValid,
  isWithinInterval,
  parse,
  startOfMonth,
  subMonths,
} from 'date-fns';
import {
  type FocusEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

export type { DateRangeValue } from '@src/shared/lib/dateRangePresets';

export type DateRangePopoverProps = {
  activeDateField?: 'from' | 'to' | null;
  autoApply?: boolean;
  from?: string | null;
  to?: string | null;
  onChange?: (value: DateRangeValue) => void;
  onApply?: (value: DateRangeValue) => void;
  onClear?: () => void;
  showActions?: boolean;
  showDateFields?: boolean;
};

export type DateRangePopoverHandle = {
  clear: () => void;
  setFromDate: (date: Date | null) => void;
  setToDate: (date: Date | null) => void;
  submit: () => void;
};

function parseApiDate(value?: string | null): Date | null {
  if (!value) {
    return null;
  }
  const parsed = parse(value, 'yyyy-MM-dd', new Date());
  if (!isValid(parsed)) {
    return null;
  }
  return parsed;
}

function formatApiDate(value: Date | null): string | null {
  if (!value) {
    return null;
  }
  return format(value, 'yyyy-MM-dd');
}

function formatInputDate(value: Date | null): string {
  return value ? format(value, DATE_FORMAT) : '';
}

function normalizeDateRange(
  fromDate: Date | null,
  toDate: Date | null,
): { from: Date | null; to: Date | null } {
  if (fromDate && toDate && fromDate > toDate) {
    return { from: toDate, to: fromDate };
  }

  return { from: fromDate, to: toDate };
}

const DateRangePopover = forwardRef<
  DateRangePopoverHandle,
  DateRangePopoverProps
>((props, ref) => {
  const {
    activeDateField = null,
    autoApply = false,
    from = null,
    to = null,
    onChange,
    onApply,
    onClear,
    showActions = true,
    showDateFields = true,
  } = props;
  const { i18n, t } = useTranslation();

  const [fromDate, setFromDate] = useState<Date | null>(() =>
    parseApiDate(from),
  );
  const [toDate, setToDate] = useState<Date | null>(() => parseApiDate(to));
  const [fromText, setFromText] = useState(() =>
    formatInputDate(parseApiDate(from)),
  );
  const [toText, setToText] = useState(() => formatInputDate(parseApiDate(to)));
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const calendarsRef = useRef<HTMLDivElement | null>(null);
  const [calendarMonth, setCalendarMonth] = useState(() =>
    startOfMonth(parseApiDate(from) ?? parseApiDate(to) ?? new Date()),
  );

  useEffect(() => {
    const nextFrom = parseApiDate(from);
    const nextTo = parseApiDate(to);
    setFromDate(nextFrom);
    setToDate(nextTo);
    setFromText(formatInputDate(nextFrom));
    setToText(formatInputDate(nextTo));
    if (nextFrom) {
      setCalendarMonth(startOfMonth(nextFrom));
      return;
    }
    if (nextTo) {
      setCalendarMonth(startOfMonth(subMonths(nextTo, 1)));
    }
  }, [from, to]);

  const mondayAdapterLocale = useMemo(() => {
    const appLanguage = mapBackendLanguageToApp(i18n.resolvedLanguage);
    const locale = getDateAdapterLocale(appLanguage);
    return {
      ...locale,
      options: {
        ...locale.options,
        weekStartsOn: 1 as const,
      },
    };
  }, [i18n.resolvedLanguage]);

  const dayOfWeekFormatter = useMemo<
    NonNullable<DateCalendarProps['dayOfWeekFormatter']>
  >(() => {
    const appLanguage = mapBackendLanguageToApp(i18n.resolvedLanguage);
    const localeTag = i18n.resolvedLanguage || getIntlLocaleTag(appLanguage);
    const formatter = new Intl.DateTimeFormat(localeTag, {
      weekday: 'short',
    });

    return (date) => {
      const label = formatter
        .format(date as Date)
        .replace(/\./g, '')
        .trim();
      return label.slice(0, 2);
    };
  }, [i18n.resolvedLanguage]);

  const setRange = (
    nextFrom: Date | null,
    nextTo: Date | null,
    changedBy?: 'from' | 'to' | 'both',
  ) => {
    const firstVisibleMonth = startOfMonth(calendarMonth);
    const secondVisibleMonth = startOfMonth(addMonths(calendarMonth, 1));
    const visibleRange = {
      start: firstVisibleMonth,
      end: endOfMonth(secondVisibleMonth),
    };
    const isVisibleInCalendars = (date: Date | null) =>
      Boolean(
        date &&
          isWithinInterval(date, {
            start: visibleRange.start,
            end: visibleRange.end,
          }),
      );

    const normalized = normalizeDateRange(nextFrom, nextTo);
    const normalizedFrom = normalized.from;
    const normalizedTo = normalized.to;

    setFromDate(normalizedFrom);
    setToDate(normalizedTo);
    setFromText(formatInputDate(normalizedFrom));
    setToText(formatInputDate(normalizedTo));
    setHoveredDate(null);
    const nextValue = {
      from: formatApiDate(normalizedFrom),
      to: formatApiDate(normalizedTo),
    };
    onChange?.(nextValue);
    if (autoApply && normalizedFrom && normalizedTo) {
      onApply?.(nextValue);
    }

    if (
      changedBy === 'from' &&
      normalizedFrom &&
      !isVisibleInCalendars(normalizedFrom)
    ) {
      setCalendarMonth(startOfMonth(normalizedFrom));
      return;
    }
    if (
      changedBy === 'to' &&
      normalizedTo &&
      !isVisibleInCalendars(normalizedTo)
    ) {
      setCalendarMonth(startOfMonth(subMonths(normalizedTo, 1)));
      return;
    }
    const fromVisible = isVisibleInCalendars(normalizedFrom);
    const toVisible = isVisibleInCalendars(normalizedTo);
    const shouldKeepCurrentMonths =
      (!normalizedFrom || fromVisible) && (!normalizedTo || toVisible);

    if (changedBy === 'both' && normalizedFrom && !shouldKeepCurrentMonths) {
      setCalendarMonth(startOfMonth(normalizedFrom));
    }
  };

  const rangeBounds = useMemo(() => {
    if (fromDate && toDate) {
      return fromDate <= toDate
        ? { start: fromDate, end: toDate }
        : { start: toDate, end: fromDate };
    }

    if (hoveredDate && ((fromDate && !toDate) || (!fromDate && toDate))) {
      const anchorDate = fromDate ?? toDate;
      if (!anchorDate) {
        return null;
      }
      return anchorDate <= hoveredDate
        ? { start: anchorDate, end: hoveredDate }
        : { start: hoveredDate, end: anchorDate };
    }

    return null;
  }, [fromDate, hoveredDate, toDate]);

  const handleCalendarSelect = (day: Date | null) => {
    if (!day) {
      return;
    }

    if (activeDateField === 'from') {
      setRange(day, null, 'from');
      return;
    }

    if (fromDate && toDate && activeDateField === 'to') {
      setRange(fromDate, day, 'to');
      return;
    }

    if (!fromDate && !toDate) {
      setRange(day, null, 'from');
      return;
    }

    if (fromDate && !toDate) {
      setRange(fromDate, day, 'to');
      return;
    }

    if (!fromDate && toDate) {
      setRange(day, toDate, 'from');
      return;
    }

    setRange(day, null, 'from');
  };

  const commitFromInput = (applyOnCommit = false) => {
    const parsed = parseDateInput(fromText, new Date()).date;
    if (fromText.trim() && !parsed) {
      setFromText(formatInputDate(fromDate));
      if (applyOnCommit) {
        onApply?.({
          from: formatApiDate(fromDate),
          to: formatApiDate(toDate),
        });
      }
      return;
    }

    const normalized = normalizeDateRange(parsed, toDate);
    setRange(parsed, toDate, 'from');
    if (applyOnCommit) {
      onApply?.({
        from: formatApiDate(normalized.from),
        to: formatApiDate(normalized.to),
      });
    }
  };

  const commitToInput = (applyOnCommit = false) => {
    const parsed = parseDateInput(toText, new Date()).date;
    if (toText.trim() && !parsed) {
      setToText(formatInputDate(toDate));
      if (applyOnCommit) {
        onApply?.({
          from: formatApiDate(fromDate),
          to: formatApiDate(toDate),
        });
      }
      return;
    }

    const normalized = normalizeDateRange(fromDate, parsed);
    setRange(fromDate, parsed, 'to');
    if (applyOnCommit) {
      onApply?.({
        from: formatApiDate(normalized.from),
        to: formatApiDate(normalized.to),
      });
    }
  };

  const shouldCommitInputBlur = (
    event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const nextFocused = event.relatedTarget as Node | null;
    if (!nextFocused) {
      return true;
    }
    return !calendarsRef.current?.contains(nextFocused);
  };

  const RangePickersDay = (dayProps: PickersDayProps) => {
    const day = dayProps.day as Date;
    const isOutsideCurrentMonth = Boolean(dayProps.outsideCurrentMonth);
    const isToday = Boolean(dayProps.today);

    const isInRange = Boolean(
      !isOutsideCurrentMonth &&
        rangeBounds &&
        (rangeBounds.start && rangeBounds.end
          ? isWithinInterval(day, {
              start: rangeBounds.start,
              end: rangeBounds.end,
            })
          : true) &&
        (!rangeBounds.start || day >= rangeBounds.start) &&
        (!rangeBounds.end || day <= rangeBounds.end),
    );
    const isRangeStart = Boolean(
      !isOutsideCurrentMonth &&
        rangeBounds?.start &&
        isSameDay(day, rangeBounds.start),
    );
    const isRangeEnd = Boolean(
      !isOutsideCurrentMonth &&
        rangeBounds?.end &&
        isSameDay(day, rangeBounds.end),
    );
    const isRangeBound = isRangeStart || isRangeEnd;
    const daySx = {
      margin: 0,
      width: '24px',
      height: '24px',
      ...(isOutsideCurrentMonth
        ? {
            bgcolor: 'transparent',
          }
        : {}),
      ...(isInRange
        ? {
            bgcolor: 'grey.200',
            '&:hover, &:focus': {
              bgcolor: 'grey.300',
            },
          }
        : {}),
      ...(isRangeBound
        ? {
            bgcolor: 'primary.main',
            color: 'common.white',
            '&:hover, &:focus': {
              bgcolor: 'primary.main',
            },
          }
        : {}),
      ...(!isOutsideCurrentMonth && !isInRange && isToday
        ? {
            color: 'primary.main',
            border: 'none',
            bgcolor: 'transparent',
            '&:hover, &:focus': {
              color: 'common.white',
              bgcolor: 'primary.main',
            },
          }
        : {}),
    };

    return (
      <Box
        sx={{
          margin: 0,
          padding: '4px',
          ...(isOutsideCurrentMonth
            ? { bgcolor: 'transparent' }
            : isInRange
              ? { bgcolor: 'grey.200' }
              : {}),
        }}
      >
        <PickersDay
          {...dayProps}
          disabled={Boolean(dayProps.disabled || isOutsideCurrentMonth)}
          selected={false}
          disableHighlightToday
          onMouseEnter={(event) => {
            dayProps.onMouseEnter?.(event, day);
            if (!isOutsideCurrentMonth) {
              setHoveredDate(day);
            }
          }}
          sx={daySx}
        />
      </Box>
    );
  };

  const handleApply = () => {
    onApply?.({
      from: formatApiDate(fromDate),
      to: formatApiDate(toDate),
    });
  };

  const handleSubmit = () => {
    const parsedFrom = parseDateInput(fromText, new Date()).date;
    const parsedTo = parseDateInput(toText, new Date()).date;
    const nextFrom = fromText.trim() && !parsedFrom ? fromDate : parsedFrom;
    const nextTo = toText.trim() && !parsedTo ? toDate : parsedTo;
    const normalized = normalizeDateRange(nextFrom, nextTo);

    setRange(normalized.from, normalized.to, 'both');
    onApply?.({
      from: formatApiDate(normalized.from),
      to: formatApiDate(normalized.to),
    });
  };

  const handleClear = () => {
    setRange(null, null, 'both');
    setCalendarMonth(startOfMonth(new Date()));
    if (onClear) {
      onClear();
      return;
    }
    onApply?.({ from: null, to: null });
  };

  useImperativeHandle(ref, () => ({
    clear: handleClear,
    setFromDate: (date) => setRange(date, toDate, 'from'),
    setToDate: (date) => setRange(fromDate, date, 'to'),
    submit: handleSubmit,
  }));

  return (
    <Box sx={styles.root}>
      <Box sx={styles.main}>
        <Box sx={styles.monthSwitcher}>
          <Box sx={styles.monthWrap}>
            <IconButton
              aria-label="Previous month"
              size="small"
              onClick={() =>
                setCalendarMonth((current) => subMonths(current, 1))
              }
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>

            <Typography sx={styles.monthTitle}>
              {format(calendarMonth, 'MMMM yyyy', {
                locale: mondayAdapterLocale,
              })}
            </Typography>
          </Box>

          <Box sx={styles.monthWrap}>
            <Typography sx={styles.monthTitle}>
              {format(addMonths(calendarMonth, 1), 'MMMM yyyy', {
                locale: mondayAdapterLocale,
              })}
            </Typography>

            <IconButton
              aria-label="Next month"
              size="small"
              onClick={() =>
                setCalendarMonth((current) => addMonths(current, 1))
              }
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={mondayAdapterLocale}
        >
          <Grid
            container
            spacing={1.25}
            ref={calendarsRef}
            onMouseLeave={() => setHoveredDate(null)}
          >
            <Grid size={6}>
              <DateCalendar
                key={format(calendarMonth, 'yyyy-MM')}
                value={null}
                referenceDate={calendarMonth}
                showDaysOutsideCurrentMonth
                dayOfWeekFormatter={dayOfWeekFormatter}
                onMonthChange={(month) => setCalendarMonth(startOfMonth(month))}
                onChange={handleCalendarSelect}
                slots={{ day: RangePickersDay }}
                sx={styles.calendar}
              />
            </Grid>
            <Grid size={6}>
              <DateCalendar
                key={format(addMonths(calendarMonth, 1), 'yyyy-MM')}
                value={null}
                referenceDate={addMonths(calendarMonth, 1)}
                showDaysOutsideCurrentMonth
                dayOfWeekFormatter={dayOfWeekFormatter}
                onMonthChange={(month) =>
                  setCalendarMonth(startOfMonth(subMonths(month, 1)))
                }
                onChange={handleCalendarSelect}
                slots={{ day: RangePickersDay }}
                sx={styles.calendar}
              />
            </Grid>
          </Grid>

          {showDateFields || showActions ? (
            <Grid container>
              {showDateFields ? (
                <Grid size={showActions ? 6 : 12}>
                  <Stack direction="row" spacing={1.25}>
                    <TextField
                      size="small"
                      label={t('common.from_label', 'From')}
                      placeholder={DATE_FORMAT.toLowerCase()}
                      value={fromText}
                      onChange={(event) => setFromText(event.target.value)}
                      onBlur={(event) => {
                        if (!shouldCommitInputBlur(event)) {
                          return;
                        }
                        commitFromInput();
                      }}
                      sx={styles.textField}
                    />
                    <TextField
                      size="small"
                      label={t('common.to_label', 'To')}
                      placeholder={DATE_FORMAT.toLowerCase()}
                      value={toText}
                      onChange={(event) => setToText(event.target.value)}
                      onBlur={(event) => {
                        if (!shouldCommitInputBlur(event)) {
                          return;
                        }
                        commitToInput();
                      }}
                      sx={styles.textField}
                    />
                  </Stack>
                </Grid>
              ) : null}

              {showActions ? (
                <Grid size={showDateFields ? 6 : 12}>
                  <Stack direction="row" spacing={1.25} sx={styles.actions}>
                    <Button
                      variant="outlined"
                      onClick={handleClear}
                      size="small"
                    >
                      {t('common.clear_all', 'Clear all')}
                    </Button>

                    <Button
                      variant="contained"
                      onClick={handleApply}
                      size="small"
                    >
                      {t('common.apply_button_title', 'Apply')}
                    </Button>
                  </Stack>
                </Grid>
              ) : null}
            </Grid>
          ) : null}
        </LocalizationProvider>
      </Box>
    </Box>
  );
});

const styles = {
  root: {
    p: 1.25,
    display: 'flex',
  },
  main: {},
  monthSwitcher: {
    display: 'flex',
    px: 1,
    py: 0.75,
  },
  monthWrap: {
    display: 'flex',
    alignItems: 'center',
    width: '50%',
  },
  monthTitle: {
    fontWeight: 500,
    flexGrow: 1,
    textAlign: 'center',
  },
  calendar: {
    height: 248,
    width: '100%',
    p: 0,
    '& .MuiPickersCalendarHeader-root': {
      display: 'none',
    },
    '.MuiDayCalendar-header': {
      justifyContent: 'start',
    },
    '& .MuiDayCalendar-weekDayLabel': {
      margin: 0,
      width: 32,
    },
    '& .MuiDayCalendar-weekContainer': {
      margin: 0,
    },
  },
  textField: {
    width: 100,
  },
  actions: {
    justifyContent: 'end',
    mt: 1.5,
  },
} as const;

DateRangePopover.displayName = 'DateRangePopover';
export default DateRangePopover;
